<template>
  <bm-dialog :label="label" @accept="accept(addressObj)" @cancel="cancel">
    <bm-alert v-if="addressLookupFailed" type="warning">
      {{ $m.smart_address_failed_to_resolve_banner }}
    </bm-alert>
    <bm-address-lookup
      :t="$m.keys.smart_address_search"
      @address-retrieved="updateAddressFromRetrieve"
      @failed-to-retrieve-address="failedToRetrieveAddress"
      @retrieving-address="updateRetrievingAddress"
    />
    <div v-for="part in partsDisplayOrder" :key="part.t.value">
      <bm-country
        v-if="part.type === 'country'"
        :value="part.read(addressObj)"
        :t="part.t"
        :disabled="retrievingAddress"
        :loading="retrievingAddress"
        clearable
        @update:value="part.write(addressObj, $event ?? '')"
      />
      <bm-dropdown
        v-else-if="part.type === 'ddl'"
        :items="getDdlOptionsWithCurrentValue(part)"
        :value="part.read(addressObj)"
        :t="part.t"
        :disabled="retrievingAddress"
        :loading="retrievingAddress"
        @update:value="part.write(addressObj, $event ?? '')"
      ></bm-dropdown>
      <bm-text
        v-else
        :value="part.read(addressObj)"
        :t="part.t"
        :autofocus="part === partsDisplayOrder[0]"
        :disabled="retrievingAddress"
        :loading="retrievingAddress"
        @update:value="part.write(addressObj, $event)"
      />
    </div>
  </bm-dialog>
</template>

<script setup lang="ts">
import {
  useReactiveDialogScriptSetup,
  type DialogEvents,
} from "@/components/mixins/dialogs";
import { ref, computed } from "vue";
import messages, { useI18n } from "@/plugins/i18n";
import BmDialog from "@/components/dialogs/BmDialog.vue";
import type {
  Address,
  AddressPart,
} from "@/components/controls/address/addresses";
import { getAddressPartDisplayOrder } from "@/components/controls/address/addresses";
import cloneDeep from "lodash-es/cloneDeep";
import BmText from "@/components/controls/input/BmText.vue";
import BmCountry from "@/components/controls/input/specialised/BmCountry.vue";
import BmAlert from "@/components/controls/BmAlert.vue";
import BmAddressLookup from "@/components/controls/address/BmAddressLookup.vue";

export interface BmAddressCaptureDialogProps {
  label?: string;
  address: Address;
  failedToRetrieve?: boolean;
}

export interface BmAddressCaptureDialogEvents extends DialogEvents<Address> {}

const props = withDefaults(defineProps<BmAddressCaptureDialogProps>(), {
  label: messages.address_capture,
  failedToRetrieve: false,
});

const emit = defineEmits<BmAddressCaptureDialogEvents>();

const { accept, cancel } = useReactiveDialogScriptSetup<Address>(emit);
const { $m } = useI18n();
const addressObj = ref<Address>(cloneDeep(props.address));
const addressLookupFailed = ref(props.failedToRetrieve);
const retrievingAddress = ref(false);

const partsDisplayOrder = computed(() =>
  getAddressPartDisplayOrder().filter((part) =>
    part.isValidFor(addressObj.value)
  )
);

const updateAddressFromRetrieve = (retrievedAddress: Address) => {
  addressObj.value = retrievedAddress;
  retrievingAddress.value = false;
};

const failedToRetrieveAddress = () => {
  addressLookupFailed.value = true;
  retrievingAddress.value = false;
};

const updateRetrievingAddress = () => {
  retrievingAddress.value = true;
  addressLookupFailed.value = false;
};

const getDdlOptionsWithCurrentValue = (part: AddressPart) => {
  const currentValue = part.read(addressObj.value);
  return part.ddlOptions.some((x) => x === currentValue)
    ? part.ddlOptions
    : [currentValue, ...part.ddlOptions];
};
</script>
